/**
 * The coaching switch (privacy policy, "Coaching (optional)"): off by default, one per founder company, with its
 * record (who flipped it and when). Only a founder turns it on, after a confirm; anyone on the team turns it off in
 * one click, which stops conversations and any paired device at once. Takes the API client and an optional founder,
 * so an admin view could mount it later; there the API lets them turn it off only.
 *
 * Below the switch, what cpg.computer holds (counts, kept 90 days) and, for the founder only, a confirmed delete of
 * all of it (`DELETE /api/founder/coaching/data`). Turning coaching off keeps what arrived until that delete.
 */
import { useState } from 'react'
import {
  COACHING_OFF_WORDS,
  COACHING_WHAT,
  deletedLine,
  deleteQuestion,
  FIRST_ON_WORDS,
  heldLine,
  historyLine,
  keptWords,
  PRIVACY_URL,
  turnOnQuestion,
  turnOnReason,
} from '../../domain/coaching'
import { when } from '../../domain/format'
import type { Api } from '../../infra/api'
import { Stack, Text } from '../../ui'
import { useLoad } from '../useLoad'
import { ConfirmAction } from './ConfirmAction'
import { Facts, Notice, Section, StatusChip } from './ui'

/** How many past flips show; the API keeps them all. */
const SHOWN = 5

export function CoachingSwitch({ api, company, founderId }: { api: Api; company: string; founderId?: string }) {
  const view = useLoad(() => api.coaching(founderId), [api, founderId])
  const [working, setWorking] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [deleted, setDeleted] = useState<{ ok: boolean; text: string } | null>(null)
  const v = view.data
  const on = v?.coaching.on ?? false

  async function flip(next: boolean) {
    setWorking(true)
    setFailed(null)
    try {
      view.set(await api.setCoaching(next, founderId))
    } catch (e) {
      setFailed(e instanceof Error ? e.message : String(e))
    } finally {
      setWorking(false)
    }
  }

  async function deleteData() {
    setDeleting(true)
    setDeleted(null)
    try {
      const { deleted: gone, ...rest } = await api.deleteCoachingData()
      view.set(rest)
      setDeleted({ ok: true, text: deletedLine(gone) })
    } catch (e) {
      setDeleted({ ok: false, text: e instanceof Error ? e.message : String(e) })
    } finally {
      setDeleting(false)
    }
  }

  return (
    <Section title="Coaching" action={v && <StatusChip status={on ? 'active' : 'pending'} label={on ? 'on' : 'off'} />}>
      <Stack gap="sm">
        {view.error && <Notice tone="bad">We couldn't load the coaching switch just now. Try again in a moment.</Notice>}
        {!v && !view.error && <Text tone="muted">One moment.</Text>}
        {v && (
          <>
            {on ? (
              <Text as="p" size="base" weight="read">
                Coaching is on for everyone at {company}. Our team may read your coding agents' conversations, including the code in them, and pair a device to your computer to
                watch your agents work and message them for you.
              </Text>
            ) : (
              <Text as="p" size="base" weight="read">Coaching is off. It covers everyone at {company}. {COACHING_WHAT}</Text>
            )}
            <Text as="p" tone="muted" size="sm">
              {COACHING_OFF_WORDS} {keptWords(v)}{' '}
              <a href={PRIVACY_URL} target="_blank" rel="noreferrer">
                Read the privacy policy
              </a>
              .
            </Text>
            {on && v.coaching.since && (
              <Facts
                rows={[
                  ['On since', when(v.coaching.since)],
                  ['Turned on by', v.coaching.by ?? 'someone who has left'],
                ]}
              />
            )}
            {failed && <Notice tone="bad">{failed}</Notice>}
            {!on && v.can.turnOn && (
              <Text as="p" tone="muted" size="sm">
                {FIRST_ON_WORDS}
              </Text>
            )}
            {on ? (
              <ConfirmAction label="Turn coaching off" tone="ink" working={working} disabled={!v.can.turnOff} reason={v.can.turnOff ? null : 'You may not change coaching.'} onConfirm={() => void flip(false)} />
            ) : (
              <ConfirmAction
                label="Turn coaching on"
                yes="Turn it on"
                tone="soft"
                question={turnOnQuestion(company)}
                working={working}
                disabled={!v.can.turnOn}
                reason={turnOnReason(v)}
                onConfirm={() => void flip(true)}
              />
            )}
            {v.received && (
              <Stack gap="xs" label="What cpg.computer holds">
                <Text as="p" size="sm">
                  {heldLine(v.received)}
                </Text>
                {/* Only the founder deletes, and only when there is something to delete. */}
                {v.can.deleteData && (v.received.sessions > 0 || v.received.messages > 0) && (
                  <ConfirmAction
                    label="Delete what cpg.computer received"
                    yes="Delete it all"
                    tone="ghost"
                    question={deleteQuestion(company, on)}
                    working={deleting}
                    onConfirm={() => void deleteData()}
                  />
                )}
                {deleted && <Notice tone={deleted.ok ? 'plain' : 'bad'}>{deleted.text}</Notice>}
              </Stack>
            )}
            {/* The record once there is more than the latest flip, which the facts above already show. */}
            {v.history.length > 1 && (
              <Stack as="ul" gap="xs" label="Coaching record">
                {v.history.slice(0, SHOWN).map((h, i) => (
                  <li key={`${h.at}-${i}`}>
                    <Text tone="muted" size="sm">
                      <Text mono size="xs" tone="muted">{when(h.at)}</Text> {historyLine(h)}
                    </Text>
                  </li>
                ))}
              </Stack>
            )}
          </>
        )}
      </Stack>
    </Section>
  )
}
